import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbArrowRight, TbCreditCard, TbHeart, TbHeartBroken } from 'react-icons/tb';
import { BiInfoCircle } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/buttons/Button';
import useActiveSubscription from '../../../hooks/community/useActiveSubscription';
import useActiveBusinessSubscription from '../../../hooks/business/useActiveBusinessSubscription';
import {
  baseSubscriptionPrices,
  variableSubscriptionPrices,
  shouldShowTrialBanner,
  normalizeMemberCount,
} from '../../../utils/subscriptionUtils';
import {
  businessBaseSubscriptionPrices,
  businessVariableSubscriptionPrices,
} from '../../../utils/BusinessFeatureUtils';
import { InvoiceData, PaymentDetails } from '../../../types/payments';
import InvoicesTable from '../../components/tables/InvoicesTable';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import communityService from '../../../services/community/communityService';
import businessService from '../../../services/business/businessService';
import { subscriptionService } from '../../../services/community/subscriptionService';
import { businessSubscriptionService } from '../../../services/business/subscriptionService';
import PageHeader from '../../components/misc/PageHeader';
import TrialBanner from '../../components/misc/TrialBanner';
import PaymentMethodIcon from '../../components/misc/PaymentMethodIcon';
import { formatDate } from '../../../utils/stringUtils';

export default function ManageSubscriptionPage(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.manageSubscription' });
  const { t: subscriptionT } = useTranslation('', { keyPrefix: 'page.subscriptions' });
  const communitySubscription = useActiveSubscription();
  const businessSubscription = useActiveBusinessSubscription();
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>();
  const [lastInvoices, setLastInvoices] = useState<InvoiceData[]>();
  const [userCount, setUserCount] = useState(0);
  const [endDate, setEndDate] = useState<Date | null>();
  const [isTrialActive, setIsTrialActive] = useState(false);
  const community = useSelectedCommunity();
  const business = useSelectedBusiness();
  const navigate = useNavigate();

  const isBusiness = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';
  const activeSubscription = isBusiness ? businessSubscription : communitySubscription;
  const entity = isBusiness ? business : community;
  const subService = isBusiness ? businessSubscriptionService : subscriptionService;

  useEffect(() => {
    if (isBusiness) {
      businessService.fetchBusinesses();
    } else {
      communityService.fetchCommunity();
    }
  }, [isBusiness]);

  useEffect(() => {
    const trialActive =
      entity?.trialEndDate && new Date(entity.trialEndDate) > new Date();
    setIsTrialActive(!!trialActive);
  }, [entity]);

  useEffect(() => {
    const fetchData = async () => {
      setPaymentDetails(undefined);
      setLastInvoices([]);
      setUserCount(0);

      try {
        const [details, invoices, usageResult, endDateRaw] = await Promise.all([
          subService.getPaymentDetails().catch(() => null),
          subService.getLastInvoices(3).catch(() => [] as InvoiceData[]),
          subService.getSubscriptionUsage().catch(() => 0 as number | { memberCount: number }),
          subService.getEndDate().catch(() => null),
        ]);
        setPaymentDetails(details);
        setLastInvoices(invoices);
        setUserCount(normalizeMemberCount(usageResult as number | { memberCount: number }));
        setEndDate(endDateRaw != null ? new Date((endDateRaw as number) * 1000) : null);
      } catch {
        setPaymentDetails(null);
      }
    };

    fetchData();
  }, [activeSubscription, entity, subService]);

  const basePrices = isBusiness ? businessBaseSubscriptionPrices : baseSubscriptionPrices;
  const variablePrices = isBusiness ? businessVariableSubscriptionPrices : variableSubscriptionPrices;
  
  let calculatedAmount = (basePrices as any)[activeSubscription] || 0;
  calculatedAmount +=
    userCount > 25 ? (variablePrices as any)[activeSubscription] * (userCount - 25) : 0;

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        badge={
          <span className="rounded-xl border-2 border-primary-900 px-4 py-0.5 text-lg font-semibold uppercase">
            {subscriptionT(`${activeSubscription.toLowerCase()}.title`)}
          </span>
        }
      />

      {entity && shouldShowTrialBanner(entity) && (
          <TrialBanner
            trialEndDate={new Date(entity.trialEndDate!)}
            plan={entity.trialType!}
            onPaymentSetup={() =>
              navigate('/subscription/upgrade', {
                state: { changePaymentMode: false, subscriptionType: activeSubscription },
              })
            }
          />
        )}

      <div className="mb-6 rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-primary-900">{t('yourSubscription')}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-bold text-primary-900">
                {subscriptionT(`${activeSubscription.toLowerCase()}.title`)}{' '}
                {isTrialActive ? `(${t('freeTrial')})` : ''}
                {entity?.subscription?.willRenew === false && (
                  <>
                    <TbArrowRight className="mx-2 inline" />
                    {subscriptionT(isBusiness ? 'business_starter.title' : 'free.title')}
                  </>
                )}
              </h2>
            </div>
            {entity?.subscription?.willRenew === true && (
              <div className="mt-2 flex flex-col gap-1 rounded-md border border-primary-300 px-2 py-1 text-base font-bold text-primary-900">
                <div className="flex items-center gap-2">
                  <BiInfoCircle className="mr-1 inline" />
                  {t('nextPaymentText', { amount: calculatedAmount.toFixed(2) })}
                </div>
                <div className="text-primary-700 ml-7 flex flex-col gap-1 text-sm font-normal">
                  {userCount > 25 && (
                    <>
                      <span>
                        {t('breakdown.base', {
                          amount: ((basePrices as any)[activeSubscription] || 0).toFixed(2),
                        })}
                      </span>
                      <span>
                        {t('breakdown.members', {
                          count: userCount - 25,
                          price: (
                            (variablePrices as any)[activeSubscription] *
                            (userCount - 25)
                          ).toFixed(2),
                        })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
            {entity?.subscription?.willRenew === false && endDate && (
              <div className="col-span-3 mt-2 rounded-md border border-secondary px-2 py-1 text-base font-bold text-secondary">
                {t('cancelledText', { date: formatDate(endDate) })}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <Button variant="primary" onClick={() => navigate('/settings/subscriptions')}>
              {t('changeSubscription')}
            </Button>
            <div className="flex flex-col justify-end gap-3 sm:flex-row">
              {entity?.subscription?.willRenew === true && (
                <Button
                  onClick={() => {
                    subService.cancelSubscription().then(() => {
                      if (isBusiness) {
                        businessService.fetchBusinesses();
                      } else {
                        communityService.fetchCommunity();
                      }
                      subService
                        .getEndDate()
                        .then((x) => {
                          if (x != null) {
                            setEndDate(new Date(x * 1000));
                          } else {
                            setEndDate(null);
                          }
                        })
                        .catch(() => setEndDate(null));
                    });
                  }}
                  variant="danger"
                  className="w-full sm:w-auto">
                  <TbHeartBroken className="h-4 w-4" />
                  {t('cancel')}
                </Button>
              )}
              {entity?.subscription?.willRenew === false && (
                <Button
                  onClick={() => {
                    subService.reactivateSubscription().then(() => {
                      if (isBusiness) {
                        businessService.fetchBusinesses();
                      } else {
                        communityService.fetchCommunity();
                      }
                    }).catch((e: any) => {
                      if (e?.status === 402) {
                        const upgradeRoute = isBusiness
                          ? '/subscription/upgrade'
                          : '/subscription/upgrade';
                        navigate(upgradeRoute, {
                          state: { changePaymentMode: true, subscriptionType: activeSubscription, isRenewal: true },
                        });
                      }
                    });
                  }}
                  variant="danger"
                  className="w-full sm:w-auto">
                  <TbHeart className="h-4 w-4" />
                  {t('reactivate')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <h3 className="mb-4 text-lg font-semibold text-primary-900">{t('lastPayments')}</h3>
        <InvoicesTable invoices={lastInvoices || []} loading={lastInvoices === undefined} />
        <div className="mt-4 flex justify-end">
          <Button
            onClick={() => navigate('/subscription/invoices')}
            variant="primary"
            className="px-6">
            {t('showAllInvoices')}
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <h3 className="mb-2 text-lg font-semibold text-primary-900">{t('paymentMethod')}</h3>
        {isTrialActive && entity?.subscription?.willRenew === false ? (
          <div className="mb-4 rounded-lg bg-secondary p-4">
            <p className="text-sm font-medium text-secondary-50">{t('trialPaymentRequired')}</p>
            <p className="mt-1 text-sm text-secondary-50">{t('trialPaymentHelper')}</p>
          </div>
        ) : (
          <p className="mb-4 text-sm text-gray-500">{t('paymentMethodHelper')}</p>
        )}
        {paymentDetails === undefined ? (
          <div className="h-16 w-full animate-pulse rounded-xl bg-secondary-200" />
        ) : (
          <PaymentDetailsRow paymentDetails={paymentDetails} isBusiness={isBusiness} />
        )}
      </div>
    </>
  );
}

type PaymentDetailsRowProps = {
  paymentDetails: PaymentDetails | null;
  isBusiness: boolean;
};

function PaymentDetailsRow({ paymentDetails, isBusiness }: PaymentDetailsRowProps) {
  const { t } = useTranslation('', { keyPrefix: 'page.manageSubscription' });
  const navigate = useNavigate();
  const communitySubscription = useActiveSubscription();
  const businessSubscription = useActiveBusinessSubscription();
  const community = useSelectedCommunity();
  const business = useSelectedBusiness();
  
  const activeSubscription = isBusiness ? businessSubscription : communitySubscription;
  const entity = isBusiness ? business : community;
  const isTrialActive =
    entity?.trialEndDate && new Date(entity.trialEndDate) > new Date();

  return (
    <div className="flex items-center justify-between rounded-xl p-4">
      <div className="flex items-center gap-3">
        {paymentDetails ? (
          <PaymentMethodIcon details={paymentDetails} t={t} />
        ) : (
          <>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-300 bg-opacity-20">
              <TbCreditCard size={20} className="text-primary-900" />
            </div>
            <p className="font-medium text-primary-900">
              {isTrialActive ? t('setupPaymentMethodForTrial') : t('noPaymentMethod')}
            </p>
          </>
        )}
      </div>
      <Button
        disabled={!entity?.subscription && !entity?.trialEndDate}
        onClick={
          isTrialActive && !paymentDetails
            ? () =>
                navigate('/subscription/upgrade', {
                  state: { changePaymentMode: false, subscriptionType: activeSubscription },
                })
            : () =>
                navigate('/subscription/upgrade', {
                  state: { changePaymentMode: true, subscriptionType: activeSubscription },
                })
        }
        variant="primary">
        {isTrialActive && !paymentDetails ? t('setupPaymentMethod') : t('editPaymentMethod')}
      </Button>
    </div>
  );
}
